import type { PlatformKind } from "./platform";

/**
 * The `beforeinstallprompt` event of Chromium browsers (not part of the TypeScript DOM library).
 */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<unknown>;
  readonly userChoice: Promise<{ readonly outcome: "accepted" | "dismissed" }>;
}

/**
 * Installation state:
 * - `installed`: the app runs from the home screen or was just installed;
 * - `promptable`: the browser offers its own install dialog (Android, desktop Chromium);
 * - `manual`: installation only through the browser menu (iOS, Firefox, Safari).
 */
export type InstallState = "installed" | "promptable" | "manual";

/** Result of an install request. */
export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/** What the UI needs for installation hints (T-072). */
export interface InstallController {
  /** Platform for the manual instructions. */
  readonly platform: PlatformKind;
  /** Returns the current state. */
  readonly getState: () => InstallState;
  /** Subscribes to state changes. */
  readonly subscribe: (listener: () => void) => () => void;
  /** Shows the install dialog of the browser, if available. */
  readonly install: () => Promise<InstallOutcome>;
}

/** Window events the controller listens to (injected for tests). */
export interface InstallEventTarget {
  addEventListener(
    type: "beforeinstallprompt" | "appinstalled",
    listener: (event: Event) => void,
  ): void;
}

/**
 * Captures the install prompt of the browser so that the app can offer it at a suitable moment
 * (spec 11.1 "Installieren"). Must be created at start, before the browser fires the event.
 */
export class BrowserInstallController implements InstallController {
  readonly platform: PlatformKind;

  private readonly listeners = new Set<() => void>();

  private deferred: InstallPromptEvent | null = null;

  private state: InstallState;

  /**
   * Creates the controller and starts listening.
   *
   * @param target - The window.
   * @param platform - Detected platform.
   * @param standalone - Whether the app already runs installed.
   */
  constructor(target: InstallEventTarget, platform: PlatformKind, standalone: boolean) {
    this.platform = platform;
    this.state = standalone ? "installed" : "manual";

    target.addEventListener("beforeinstallprompt", (event) => {
      // Keep the event: the app offers installation on its own screen instead of a mini-infobar.
      event.preventDefault();
      this.deferred = event as InstallPromptEvent;
      this.setState(this.state === "installed" ? "installed" : "promptable");
    });
    target.addEventListener("appinstalled", () => {
      this.deferred = null;
      this.setState("installed");
    });
  }

  /** @inheritdoc */
  readonly getState = (): InstallState => this.state;

  /** @inheritdoc */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    const unsubscribe = (): void => {
      this.listeners.delete(listener);
    };

    return unsubscribe;
  };

  /** @inheritdoc */
  readonly install = async (): Promise<InstallOutcome> => {
    const deferred = this.deferred;

    if (deferred === null) {
      return "unavailable";
    }

    // A prompt event can be used only once.
    this.deferred = null;
    await deferred.prompt();
    const choice = await deferred.userChoice;

    if (choice.outcome === "accepted") {
      this.setState("installed");
    } else {
      this.setState("manual");
    }

    const outcome: InstallOutcome = choice.outcome;

    return outcome;
  };

  private setState(state: InstallState): void {
    if (this.state === state) {
      return;
    }

    this.state = state;

    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * Creates a controller without browser events (tests, unsupported environments).
 *
 * @param platform - Platform for the manual instructions.
 * @param state - Fixed state.
 * @returns The controller.
 */
export function createStaticInstallController(
  platform: PlatformKind,
  state: InstallState = "manual",
): InstallController {
  const controller: InstallController = {
    platform,
    getState: () => state,
    subscribe: () => () => undefined,
    install: () => Promise.resolve("unavailable"),
  };

  return controller;
}
